import React, { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, RemoteTrack, Track, RemoteParticipant, RemoteTrackPublication } from 'livekit-client';
import { getCameraViewerToken } from '../../api/cameras';

interface Props {
  deviceId: string;
  className?: string;
  /** Auto-refresh the token before expiry. Default: true. */
  autoRenew?: boolean;
}

/**
 * Subscribes to the LiveKit room a camera ingress publishes into and renders
 * the remote video track. Auto-refreshes the token before TTL.
 *
 * Used on PropertyDetail's Cameras tab and on the standalone Cameras page.
 * Mobile uses the same /viewer-token endpoint via the native LiveKit SDK.
 */
export const CameraPlayer: React.FC<Props> = ({ deviceId, className, autoRenew = true }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const roomRef  = useRef<Room | null>(null);
  const [status, setStatus] = useState<'connecting' | 'live' | 'idle' | 'error'>('connecting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let renewTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = async () => {
      try {
        setStatus('connecting');
        const { token, livekit_host } = await getCameraViewerToken(deviceId, 3600);
        if (cancelled) return;

        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;

        const attach = (track: RemoteTrack) => {
          if (track.kind === Track.Kind.Video && videoRef.current) {
            track.attach(videoRef.current);
            setStatus('live');
          } else if (track.kind === Track.Kind.Audio && audioRef.current) {
            track.attach(audioRef.current);
          }
        };
        room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _p: RemoteParticipant) => attach(track));
        room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => track.detach().forEach((el) => el.remove()));
        room.on(RoomEvent.Disconnected, () => setStatus('idle'));

        await room.connect(livekit_host, token);

        // If the ingress is already publishing, attach existing tracks
        Array.from(room.remoteParticipants.values()).forEach((p) => {
          Array.from(p.trackPublications.values()).forEach((pub) => {
            if (pub.track) attach(pub.track);
          });
        });

        if (autoRenew) {
          // Renew at 80% of TTL (3600s default → 2880s = 48m)
          renewTimer = setTimeout(() => connect(), 48 * 60 * 1000);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? String(e));
        setStatus('error');
      }
    };

    connect();
    return () => {
      cancelled = true;
      if (renewTimer) clearTimeout(renewTimer);
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, [deviceId, autoRenew]);

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className ?? ''}`}>
      <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
      <audio ref={audioRef} autoPlay />
      {status !== 'live' && (
        <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40">
          {status === 'connecting' && 'Connecting…'}
          {status === 'idle' && 'No active stream'}
          {status === 'error' && (error ?? 'Stream error')}
        </div>
      )}
    </div>
  );
};
